import type { ITour } from '@talos/kyoko';

/** Add new tour names at the top (newest `releasedInVersion` first). */
export const POSITIONS_MARKET_ACCOUNTS_TAB_TEST_ID = 'positions-market-accounts-tab';

export const TourName = {
  v130MarketAccountsTab: 'v13.0.0 Market Accounts Tab',
  v130IntelOverview: 'v13.0.0 Intel Overview',
  v120MarketDataCardMultilegQuickMarketSelector: 'v12.0 Market Data Card Multileg Quick Market Selector',
  v120PriceGridMultilegQuickMarketSelector: 'v12.0 Price Grid Multileg Quick Market Selector',
  v100PriceGrid: 'v10.0 Price Grid',
  v71PriceGridLadderToggle: 'v7.1 Price Grid Ladder Toggle',
  v70MarketCardUpdate: 'v7.0 Market Card Update',
  v70MarketSelectorDefaultAggregation: 'v7.0 Market Selector Default Aggregation',
  v50TransfersHeaderButton: 'v5.0 Transfers Header Button',
  v256IWouldSection: 'v2.56.0 I Would Section',
  v257MarketCardUpdate: 'v2.57.0 Market Card Update',
  v255Delta1BPSToggle: 'v2.55.0 Delta-1 BPS Toggle',
  v255AccessShowcaseFromWhatsNew: 'v2.55.0 Access showcase from Whats New',
  v255UpdatedNavigationSidebarPin: 'v2.55.0 Updated navigation sidebar pin',
  v254Delta1DefaultQty: 'v2.54.0 Delta1 Default Qty',
  v253MarketSelector: 'v2.53.3 Market Selector',
  v253MarketSelectorAdvancedFeatures: 'v2.53.3 Market Selector Advanced Features',
  v252FlexibleLayoutPopouts: 'v2.52.3 Flexible layout popouts',
  v249FlexibleLayoutBasics: 'v2.49.1 Flexible layout basics',
  v249DDHOrderForm: 'v2.49.0 DDH Order Form',
  v249DDHOrderBlotter: 'v2.49.0 DDH Order Blotter', // upon removal remove from AgGridSecurity.tsx
  v249DDHOrderBlotterFilledColumn: 'v2.49.0 DDH Order Blotter Filled Column', // upon removal remove from AgGridSecurity.tsx
  v249DDHOrderBlotterMenu: 'v2.49.0 DDH Order Menu',
  v249DDHOrderDetailsViewParentOrder: 'v2.49.0 DDH Order Details View Parent Order',
  v249DDHOrderDetailsViewHedgeOrder: 'v2.49.0 DDH Order Details View Hedge Order',
} as const;
export type TourName = (typeof TourName)[keyof typeof TourName];

export const tours: Record<string, ITour> = {
  [TourName.v130MarketAccountsTab]: {
    releasedInVersion: '13.0.0',
    steps: [
      {
        title: 'Market Accounts',
        description:
          'The Unified tab under Positions is now called Market Accounts. It now includes features and filter options from the legacy Derivatives tab.',
        placement: 'bottom',
        anchor: `[data-testid="${POSITIONS_MARKET_ACCOUNTS_TAB_TEST_ID}"]`,
      },
    ],
  },
  [TourName.v130IntelOverview]: {
    releasedInVersion: '13.0.0',
    steps: [
      {
        title: 'Market Metrics',
        description: "Track aggregated metrics across your asset universe, or drill into a single asset's history.",
        placement: 'right',
        anchor: '[data-testid="aggregated-asset-metric-panel"]',
      },
      {
        title: 'Relative Performance & Leaderboard',
        description: "Compare asset performance over time, and see today's gainers, volume, and market cap leaders.",
        placement: 'left',
        anchor: '[data-testid="relative-performance-panel"]',
      },
      {
        title: 'Funding Rates',
        description:
          'View funding rate history as a chart, or the current rates across exchanges in the blotter below.',
        placement: 'right',
        anchor: '[data-testid="historical-funding-rate-panel"]',
      },
      {
        title: 'Pre-configured Spaces',
        description: 'Browse pre-built dashboards for Leaderboard, Funding Rates, Stablecoins, and Exchange Flows.',
        placement: 'right',
        anchor: '#intel-sidebar-submenu-items',
      },
      {
        title: 'Build your own',
        description: 'Create a new Space to build a custom dashboard with the widgets you need.',
        placement: 'top',
        anchor: '[data-testid="new-space-button"]',
      },
    ],
  },
  [TourName.v120MarketDataCardMultilegQuickMarketSelector]: {
    releasedInVersion: '12.0',
    steps: [
      {
        title: 'Edit Multileg Leg Markets',
        description:
          'Edit the leg markets on a multileg symbol directly from the Market Data Card. Editing the leg markets will update the symbol for everyone.',
        placement: 'top',
        anchor: '[data-testid="market-card-multileg-quick-market-selector-tour-anchor"]',
      },
    ],
  },
  [TourName.v120PriceGridMultilegQuickMarketSelector]: {
    releasedInVersion: '12.0',
    steps: [
      {
        title: 'Edit Multileg Leg Markets',
        description:
          'Edit the leg markets on a multileg symbol directly from the Price Grid. Editing the leg markets will update the symbol for everyone.',
        placement: 'top',
        anchor: '[data-tour="price-grid-multileg-quick-market-selector-tour-anchor"]',
      },
    ],
  },
  [TourName.v100PriceGrid]: {
    releasedInVersion: '10.0',
    steps: [
      {
        title: 'Price Columns',
        description: 'Add price columns to view rates for additional markets and aggregations.',
        placement: 'bottom',
        anchor: '[data-testid="price-grid-add-price-column-tour-anchor"]',
      },
      {
        title: 'Currency and Base Pair filter.',
        description:
          'Filter for all symbols for a certain currency, or by Base Pair, filtering for all symbols of the Base/Quote currency pair e.g. BTC-USD will show BTC-USD spot, all BTC-USD Perps, Futures, etc.',
        placement: 'bottom',
        anchor: '[data-testid="price-grid-symbol-filter-tour-anchor"]',
      },
      {
        title: 'Price Grid Settings.',
        description:
          'Configure settings for the price grid, enabling/disabling best bid offer highlighting, show/hide the RFQ button or enable/disable price alignment on the decimal',
        placement: 'bottom-end',
        anchor: '[data-testid="price-grid-settings-menu"]',
      },
    ],
  },
  [TourName.v71PriceGridLadderToggle]: {
    releasedInVersion: '7.1',
    steps: [
      {
        title: 'Ladder v Book',
        description:
          'Toggle to change expanded rows to a Ladder or Book view of the market, in the same manner as the market card.',
        placement: 'top',
        anchor: '[data-testid="price-grid-ladder-toggle"]',
      },
    ],
  },
  [TourName.v70MarketCardUpdate]: {
    releasedInVersion: '7.0',
    steps: [
      {
        title: 'Market Card Update',
        description:
          'Access the Symbol Selector from the symbol row. You can also open the Market Selector from the market account button or the Market Card menu.',
        placement: 'top',
        anchor: '[data-testid="market-card-symbol-selector-tour-anchor"]',
      },
    ],
  },
  [TourName.v70MarketSelectorDefaultAggregation]: {
    releasedInVersion: '7.0',
    steps: [
      {
        title: 'Default Aggregation',
        description:
          'Flag an aggregation as default. The default aggregation will be selected when creating a new market card, priming orders or a DeepDive from the WatchList or selecting/changing symbol on New Order, New RFQ, Market Card and the DeepDive.',
        placement: 'right-start',
        anchor: '[data-testid="market-selector-panel-presets-list"]',
      },
    ],
  },
  [TourName.v50TransfersHeaderButton]: {
    releasedInVersion: '5.0',
    steps: [
      {
        title: 'Transfers',
        description: 'The Transfers button has moved to the header, and you can access it here going forward.',
        placement: 'bottom-end',
        anchor: '[data-testid="header-button-transfers"]',
      },
    ],
  },
  [TourName.v256IWouldSection]: {
    releasedInVersion: '2.56',
    steps: [
      {
        title: 'I Would',
        description:
          'You can now apply I-Would parameters to trajectory-based algos to opportunistically take LP quotes that are equal to or better than a configurable reference price on exchanges.',
        placement: 'left-start',
        anchor: '[data-testid="oms-strategy-section-I Would"]',
      },
    ],
  },
  [TourName.v257MarketCardUpdate]: {
    releasedInVersion: '2.57',
    steps: [
      {
        title: 'Market Card Update',
        description: 'Access the Market Selector from here or the Market Card menu.',
        placement: 'bottom',
        anchor: '[data-testid="market-card-market-acct-button"]',
      },
    ],
  },
  [TourName.v255Delta1BPSToggle]: {
    releasedInVersion: '2.55',
    steps: [
      {
        title: 'Delta-1 BPS Pricing',
        description: 'Toggle this to display Delta-1 spread prices in basis points (BPS) instead of absolute values.',
        placement: 'bottom',
        anchor: '[data-testid="delta-1-bps-toggle"]',
      },
    ],
  },
  [TourName.v255AccessShowcaseFromWhatsNew]: {
    releasedInVersion: '2.55',
    steps: [
      {
        title: 'Access showcase from Whats New',
        description: "Click here to access the showcase again from What's New.",
        placement: 'right-start',
        anchor: '[data-testid="sidebar-release-notes-item"]',
      },
    ],
  },
  [TourName.v255UpdatedNavigationSidebarPin]: {
    releasedInVersion: '2.55',
    steps: [
      {
        title: 'Updated Navigation Sidebar',
        description: 'Click here to pin the navigation sidebar.',
        placement: 'right-start',
        anchor: '[data-testid="sidebar-pin-button"]',
      },
    ],
  },
  [TourName.v254Delta1DefaultQty]: {
    releasedInVersion: '2.54',
    steps: [
      {
        title: 'Delta1 Default Qty',
        description:
          'The Qty on Delta-1 symbols, where neither leg is spot, can now be entered in Base OR Contract terms.',
        placement: 'top-end',
        anchor: '[data-testid="oms-quantity-input"]',
      },
    ],
  },
  [TourName.v253MarketSelector]: {
    releasedInVersion: '2.53',
    steps: [
      {
        title: 'Market Selector',
        description:
          'Click here or on the plus button to select an aggregation or add new market accounts to the selection.',
        placement: 'left-start',
        anchor: '[data-testid="market-selector-main-select-button"]',
      },
      {
        title: 'Market Selector',
        description: 'Click select/clear all to select all markets or clear the selection.',
        placement: 'top-start',
        anchor: '[data-testid="clear-all-select-all-button"]',
      },
      {
        title: 'Market Selector',
        description:
          'Click X beside a market to remove it from the selection. Click Only to unselect all other markets.',
        placement: 'left-start',
        anchor: '[data-testid="market-selection-item-delete"]',
      },
    ],
  },
  [TourName.v253MarketSelectorAdvancedFeatures]: {
    releasedInVersion: '2.53',
    steps: [
      {
        title: 'Market Selector',
        description: 'Enable Advanced Mode to view Max Buy, Max Sell and Unsettled values for the market account.',
        placement: 'bottom-start',
        anchor: '[data-testid="market-selector-advanced-mode-toggle"]',
      },
      {
        title: 'Market Selector',
        description:
          'Click here and enable the "Show disabled items" toggle to view market accounts that are not available and the reason for it.',
        placement: 'bottom-start',
        anchor: '[data-testid="market-selector-show-disabled-items-toggle"]',
      },
    ],
  },
  [TourName.v252FlexibleLayoutPopouts]: {
    releasedInVersion: '2.52',
    steps: [
      {
        title: 'Popouts',
        description: 'Click here to open a panel in a new window.',
        readMoreURL: 'https://kb.talostrading.com/principal-trading/flexible-ui/#popouts',
        placement: 'left-start',
        anchor: '.dv-view-container .dv-view:nth-child(2) [data-testid="popout-layout-group-button"]',
      },
      {
        title: 'Popouts',
        description: 'You can also pop out a panel by right-clicking on the tab and selecting "Popout".',
        readMoreURL: 'https://kb.talostrading.com/principal-trading/flexible-ui/#popouts',
        placement: 'bottom-end',
        anchor: '[data-testid="orders-layout-tab"]',
      },
    ],
  },
  [TourName.v249FlexibleLayoutBasics]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Flexible Layout',
        description: 'Customize your layout by dragging a tab or groups of tabs to a new location.',
        readMoreURL: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        placement: 'right-start',
        anchor: '.dv-view:nth-child(2) .dv-tabs-container',
      },
      {
        title: 'Flexible Layout',
        description: 'Switch between layouts and reset your layout via the Trading Layout menu.',
        readMoreURL: 'https://kb.talostrading.com/principal-trading/flexible-ui',
        placement: 'bottom-end',
        anchor: '[data-testid="layout-menu-button"]',
      },
    ],
  },
  [TourName.v249DDHOrderForm]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description:
          'Dynamic Delta Hedging will calculate the corresponding delta of each fill and auto-hedge using the selected perp/future and strategy.',
        placement: 'left-start',
        anchor: '[data-testid="order-form-ddh-section"]',
      },
    ],
  },
  [TourName.v249DDHOrderBlotter]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description: 'This Icon indicates that Dynamic Delta Hedging is enabled on an order.',
        placement: 'right-start',
        anchor: '[data-testid="ddh-order-icon"]',
      },
    ],
  },
  [TourName.v249DDHOrderBlotterFilledColumn]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description:
          'The Filled % cell for an order with Dynamic Delta Hedging enabled will display a DDH Summary in the tooltip.',
        placement: 'right-start',
        anchor: '.ddh-order-meter',
        offset: [-4, 8],
      },
    ],
  },
  [TourName.v249DDHOrderBlotterMenu]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description:
          'The Hedge orders generated for an order with Dynamic Delta Hedging enabled can be shown or hidden via a toggle in the Order Blotter menu.',
        placement: 'auto-start',
        anchor: '[data-testid="order-options-menu"]',
      },
    ],
  },
  [TourName.v249DDHOrderDetailsViewParentOrder]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description: 'View the Parent Order for this Hedge Order.',
        placement: 'left-start',
        anchor: '[data-testid="view-ddh-parent-order"]',
        offset: [-4, 8],
      },
    ],
  },
  [TourName.v249DDHOrderDetailsViewHedgeOrder]: {
    releasedInVersion: '2.49',
    steps: [
      {
        title: 'Dynamic Delta Hedging',
        description: 'View all Hedge Orders generated as part of Dynamic Delta Hedging for the order.',
        placement: 'left-start',
        anchor: '[data-testid="view-ddh-orders"]',
        offset: [-4, 8],
      },
    ],
  },
};
